import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tag from 'components/Tag';
import { useTranslation } from 'react-i18next';
import ButtonLoading from 'components/ButtonLoading';
import { POD_STATES } from 'utils/constants/jobDetails';
import useModal from 'hooks/useModal/useModal';
import Modal from 'components/Modal';
import PodModal from 'components/PodModal';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';
import uniqBy from 'lodash.uniqby';
import { toast } from 'react-toastify';
import Notification from 'components/Notification';
import { NOTIFICATION_TYPES } from 'utils/constants/notifications';
import { usePendingPods } from 'hooks/usePendingPods/usePendingPods';

const PodStatus = ({
  className,
  stops,
  full_status,
  journeyId,
  refetchData,
  journeyStatus
}) => {
  const { t } = useTranslation('Pods');
  const { isShowing, toggle: toggleModal } = useModal();
  const [filesToSend, setFilesToSend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { refetchPendingPods } = usePendingPods();
  let history = useHistory();
  let location = useLocation();

  useEffect(() => {
    if (
      journeyStatus === 'DONE' &&
      location.pathname !== `${ROUTES.DONE_DETAIL.DEFAULT}/${journeyId}`
    ) {
      history.push(`${ROUTES.DONE_DETAIL.DEFAULT}/${journeyId}`);
    }
  }, [journeyStatus, history, journeyId, location.pathname]);

  const dropOffStops = stops.filter(
    stop =>
      stop.stop_type === 'DROPOFF' &&
      (stop.status === 'DONE_WITHOUT_MANIFEST' || stop.status === 'DONE')
  );
  const hasPendingPods = stops.some(
    stop => stop.status === 'DONE_WITHOUT_MANIFEST'
  );

  const addUrlToSend = useCallback(
    func => {
      setFilesToSend([...filesToSend, func]);
    },
    [filesToSend]
  );

  const removeUrlToSend = useCallback(
    (fileName, title) => {
      const newArrFilesToSend = filesToSend.filter(
        item => item.fileName !== fileName || item.stopTitle !== title
      );
      setFilesToSend([...newArrFilesToSend]);
    },
    [filesToSend]
  );

  const uploadFiles = useCallback(async () => {
    toggleModal();
    const flattenFilesToSend = uniqBy(filesToSend, 'stopTitle');
    setError(false);
    for (const funcObj of flattenFilesToSend) {
      try {
        setLoading(true);
        await funcObj.func();
      } catch (e) {
        console.error('Error sending files', e);
        setError(true);
      } finally {
        setLoading(false);
        refetchData();
      }
    }
    if (!error) {
      sendEventToSegment(TRACKING_EVENTS.UPLOADED_POD, {
        journeyId
      });
      toast(
        <Notification
          title={t('successTitle')}
          type={NOTIFICATION_TYPES.SUCCESS}
          content={t('successAlert')}
        />,
        { autoClose: 5000, position: toast.POSITION.TOP_LEFT }
      );
      refetchPendingPods();
    }
    setFilesToSend([]);
  }, [
    filesToSend,
    toggleModal,
    journeyId,
    refetchData,
    error,
    t,
    refetchPendingPods
  ]);

  const handleOpenModal = useCallback(() => {
    toggleModal();
    sendEventToSegment(TRACKING_EVENTS.OPENED_POD_MODAL);
  }, [toggleModal]);

  if (dropOffStops.length === 0 || journeyStatus !== 'DONE_WITHOUT_MANIFEST')
    return null;

  return (
    <div className={className} data-testid="pod-status">
      <div className="pod-status">
        <div className="pod-status__title">{t('pods')}</div>
        <div className="pod-status__container">
          {dropOffStops.map((stop, index) => {
            return (
              <div className="pod-status__document" key={`manifest-${index}`}>
                <span className="pod-status__company-name">
                  {stop.address?.company_name}
                </span>
                {stop.status === 'DONE_WITHOUT_MANIFEST' && (
                  <Tag text={t(POD_STATES.PENDING)} skin="red" />
                )}
                {stop.status === 'DONE' && (
                  <Tag text={t(POD_STATES.DONE)} skin="grey" />
                )}
              </div>
            );
          })}
        </div>
        {hasPendingPods && (
          <>
            <ButtonLoading
              testId="pod-status__upload"
              extraClass="pod-status__upload"
              text={t('uploadAction')}
              handleClick={handleOpenModal}
              loading={loading}
            />
            <Modal isShowing={isShowing} hide={toggleModal}>
              <PodModal
                stops={stops}
                cargo_manifest={full_status?.next_requirements?.cargo_manifest}
                addFuctionToUpload={addUrlToSend}
                removeUrlToSend={removeUrlToSend}
                uploadFiles={uploadFiles}
                closeModal={toggleModal}
                disabled={filesToSend.length === 0}
              />
            </Modal>
          </>
        )}
      </div>
    </div>
  );
};
PodStatus.propTypes = {
  className: PropTypes.string,
  stops: PropTypes.array,
  full_status: PropTypes.object,
  journeyId: PropTypes.number.isRequired,
  refetchData: PropTypes.func.isRequired,
  journeyStatus: PropTypes.string.isRequired
};
export default PodStatus;
